import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from 'src/components/Container';
import { StickyCardSkeleton } from 'src/components/StickyCard';
import { Typography } from 'src/components/Typography';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { useHQPermissionCheck } from 'src/shared/HQ/useHQPermissionCheck';
import toast from 'src/utils/toast';

import {
  CreateFranchiseFeePayerParams,
  FranchiseFeePayersQueryKey,
  UpdateFranchiseFeePayerParams,
  createFranchiseFeePayer,
  fetchFranchiseFeePayer,
  updateFranchiseFeePayer,
} from '../../../models/hq/franchise-fee-payer/franchise-fee-payer';

import FranchiseFeePayerForm from './FranchiseFeePayerForm';

const FranchiseFeePayerFormPage: React.FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFeePayers',
  });
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canEditHQFranchiseFees } = useHQPermissionCheck();
  const queryClient = useQueryClient();

  // Redirect if user doesn't have edit permission

  const isEdit = id !== 'new';
  const isCreate = !isEdit;

  const {
    data: payer,
    isLoading,
    error,
  } = useQuery({
    queryKey: [FranchiseFeePayersQueryKey, id],
    queryFn: () => fetchFranchiseFeePayer(id!),
    enabled: isEdit,
  });

  const createMutation = useMutation({
    mutationFn: createFranchiseFeePayer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FranchiseFeePayersQueryKey] });
      toast.success(t('payerCreated'));
      navigate('/franchise-fees/payers');
    },
    onError: () => {
      toast.error(t('errorCreating'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateFranchiseFeePayer,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [FranchiseFeePayersQueryKey] });
      queryClient.invalidateQueries({
        queryKey: [FranchiseFeePayersQueryKey, data.id],
      });
      toast.success(t('payerUpdated'));
      navigate('/franchise-fees/payers');
    },
    onError: () => {
      toast.error(t('errorUpdating'));
    },
  });

  const handleSave = (
    values:
      | CreateFranchiseFeePayerParams
      | Omit<UpdateFranchiseFeePayerParams, 'id'>
  ) => {
    if (isCreate) {
      createMutation.mutate(values as CreateFranchiseFeePayerParams);
    } else {
      updateMutation.mutate({
        ...values,
        id: id!,
      } as UpdateFranchiseFeePayerParams);
    }
  };
  if (!canEditHQFranchiseFees()) {
    navigate('/franchise-fees/payers');
    return null;
  }

  const handleCancel = () => {
    navigate('/franchise-fees/payers');
  };

  const title = isCreate ? t('createPayer') : t('editPayer');

  return (
    <>
      <AppFrameConfig
        id="franchise-fee-payer-form"
        containerMaxWidth={'xl'}
        title={title}
        backButtonLinkTo="/franchise-fees/payers"
        backButtonTitle={t('franchiseFeePayers')}
      />
      <Container>
        {isEdit && isLoading && (
          <StickyCardSkeleton title={t('basicInformation')} sx={{ mb: 4 }} />
        )}

        {isEdit && (error || !payer) && (
          <Typography>{t('payerNotFound')}</Typography>
        )}

        {(!isEdit || (!isLoading && !error && payer)) && (
          <FranchiseFeePayerForm
            payer={isEdit ? payer : undefined}
            onSave={handleSave}
            onCancel={handleCancel}
            title={title}
            isNew={isCreate}
          />
        )}
      </Container>
    </>
  );
};

export default FranchiseFeePayerFormPage;
